import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {InfoIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef, useState} from 'react'
import {OverviewSidePanel} from './OverviewSidePanel'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {useDescriptionLiveUpdates} from '../../hooks/use-description-live-updates'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ensurePreviousActiveDialogIsClosed} from '@github-ui/diff-lines/ensure-previous-active-dialog-is-closed'

/**
 * Renders a button that opens the overview side panel
 */
export function OpenOverviewSidePanelButton({aliveChannel}: {aliveChannel: string}) {
  const [overviewSidePanelIsOpen, setOverviewSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // eslint-disable-next-line github/unescaped-html-literal, @github-ui/github-monorepo/no-cast-as-safe-html-string -- Known safe static fallback content
  const emptyDescription = `<p class="text-italic">No description provided.</p>` as SafeHTMLString

  const {data: description, isLoading} = useDescriptionData()
  useDescriptionLiveUpdates(aliveChannel)
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string */
  const bodyHtml = (description?.bodyHtml || emptyDescription) as SafeHTMLString

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenOverviewPanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The overview side panel cannot currently be opened."
            icon={InfoIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <IconButton
          ref={toggleSidePanelRef}
          aria-label="Open overview side panel"
          icon={InfoIcon}
          size="small"
          onClick={() => {
            sendPullRequestAnalyticsEvent('overview_side_panel.open', 'OVERVIEW_SIDE_PANEL_BUTTON')
            ensurePreviousActiveDialogIsClosed()
            setOverviewSidePanelIsOpen(true)
          }}
        />
        {overviewSidePanelIsOpen && (
          <OverviewSidePanel
            bodyHtml={bodyHtml}
            isLoading={isLoading}
            onClose={() => setOverviewSidePanelIsOpen(false)}
          />
        )}
      </ErrorBoundary>
    </div>
  )
}
